'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import cx from 'classnames';
import { CATCORP_ESITE, PAGE_BREAKPOINTS } from 'constants/common';
import {
  Conditional,
  NonReturnableIcon,
  useBreakpoint
} from '@cat-ecom/pcc-components';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CatDrawer } from 'blocks-nextjs/blocks-react/components/Drawer';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import ContactUsOptions from 'components/contact-us-options/contact-us-options';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { EspotMarketingData } from 'types/espotApi';
import styles from './return-policy-drawer.module.scss';

export interface ReturnPolicyDrawerProps {
  espotData: EspotMarketingData;
  esite: string;
  dealerPhone: string;
  helpCenterContactSupportUrl: string;
  classNameTextButton?: string;
  buttonSize?: 'xs' | 'md' | 'lg';
  isButtonAsLink?: boolean;
  withDotAtEnd?: boolean;
  dealerName: string;
  orientation?: 'side' | 'bottom';
  isUpdatePDPHeaderMobileFlag?: boolean;
  isPDPPage?: boolean;
}

const ReturnPolicyDrawer = ({
  espotData,
  esite,
  dealerPhone,
  helpCenterContactSupportUrl,
  classNameTextButton,
  isButtonAsLink,
  buttonSize,
  withDotAtEnd,
  dealerName,
  orientation = 'side',
  isUpdatePDPHeaderMobileFlag = false,
  isPDPPage = false
}: ReturnPolicyDrawerProps) => {
  const t = useTranslations();
  const [drawerActive, setDrawerActive] = useState(false);
  const dealerLocatorDrawer = useDealerLocatorDrawer();

  const isCatcorp = esite === CATCORP_ESITE;
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dealerReturnPolicyTitle = replaceTokensWithComponents(
    t('RETURN_POLICY_DEALER', { '0': '{0}' }),
    <span>{dealerName}</span>
  );

  const dealerReturnPolicyContent = replaceTokensWithComponents(
    t('NON_RETURNABLE_PART_STATEMENT', { '0': '{0}' }),
    <NonReturnableIcon />
  );

  const dealerReturnPolicy =
    !isCatcorp &&
    espotData?.marketingData?.[0].DEALER_RETURN_POLICY_espot?.contents?.[0]
      ?.html;

  const dealerReturnPolicyLink =
    !isCatcorp &&
    espotData?.marketingData?.[0].RETURN_POLICY_espot?.contents?.[0]?.html;

  const handleCloseDrawer = () => {
    setDrawerActive(false);
  };

  return (
    <>
      <CatButton
        onClick={() => {
          setDrawerActive(true);
        }}
        size={buttonSize}
        variant={'ghost'}
        className={cx({
          [styles['warranty-return-policy__mobile-btn']]:
            isPDPPage && isUpdatePDPHeaderMobileFlag && !isMD,
          [styles['warranty-return-policy__btn']]: !(
            isPDPPage &&
            isUpdatePDPHeaderMobileFlag &&
            !isMD
          ),
          [styles['warranty-return-policy__btn-link']]: isButtonAsLink
        })}
      >
        <span className={classNameTextButton}>
          {t('RETURN_POLICY_TEXT')}
          {withDotAtEnd ? '.' : ''}
        </span>
      </CatButton>
      {drawerActive && (
        <CatDrawer
          isActive={drawerActive}
          data-testid="return-policy-drawer"
          onBlDrawerClose={handleCloseDrawer}
          orientation={orientation}
          className={cx({
            [styles['return-policy-drawer__header']]: orientation === 'bottom'
          })}
          verticalMaxHeight="large"
        >
          <div slot="header" className="cat-u-theme-typography-title-sm">
            {t('RETURN_POLICY_INFO')}
          </div>
          <div className="cat-u-theme-typography-body">
            {dealerReturnPolicyContent}
          </div>

          <Conditional test={!!dealerReturnPolicy || !!dealerReturnPolicyLink}>
            <div
              className={cx(styles['dealerReturnPolicy__container'], {
                [styles['dealerReturnPolicy__page-view']]: !isCatcorp
              })}
            >
              <Conditional test={!!dealerReturnPolicy}>
                <CatHeading
                  level="h3"
                  variant="headline"
                  className="text-capitalize"
                >
                  {dealerReturnPolicyTitle}
                </CatHeading>
                <p
                  className="cat-u-margin-bottom-none text-break"
                  dangerouslySetInnerHTML={{ __html: dealerReturnPolicy }}
                />
              </Conditional>
              <Conditional test={!!dealerReturnPolicyLink}>
                <div
                  dangerouslySetInnerHTML={{ __html: dealerReturnPolicyLink }}
                />
              </Conditional>
            </div>
          </Conditional>
          <ContactUsOptions
            esite={esite}
            dealerPhone={dealerPhone}
            helpCenterContactSupportUrl={helpCenterContactSupportUrl}
            onClickFindDealer={() => dealerLocatorDrawer.openDrawer()}
          />
        </CatDrawer>
      )}
    </>
  );
};

export default ReturnPolicyDrawer;
